import PhoneLib from 'google-libphonenumber';
import { isValidNumberForRegion } from 'libphonenumber-js';
import Logger from 'logger';
import { Middleware } from 'redux';

import { allocateNumber } from 'services/api/api.contact';
import { sipGateway } from 'services/api/api.sipGateway';

import AnyAction from 'store/anyActions';
import AppTypes from 'store/app/app.types';
import RootState from 'store/state';
import CallTypes from 'store/call/call.types';
import { addError, setChangingState } from 'store/global/global.actions';
import { updateContactAttributes } from 'store/contact/contact.actions';
import UserTypes from 'store/user/user.types';
import { WebSocketCompanionActions } from 'store/websocketCompanion/websocketCompanion.types';

import { formatPhoneNumber, getNumberToDialThroughConnect, matchPattern } from 'utils';

import { recoverContacts } from './ccp.recoverContacts';
import listenToAgent from './listeners/listeners.agent';

let getAgent: () => connect.Agent;

const ccpAgentMiddleware: Middleware<{}, RootState> = store => next => async (action: AnyAction) => {
    if (store.getState().app?.callProvider === 'kumodi') {
        return next(action);
    }

    const { websocketCompanion } = store.getState();

    switch (action.type) {
        case AppTypes.SELECT_INSTANCE:
            getAgent = listenToAgent(store);
            return next(action);
        case UserTypes.USER_AUTHENTICATION_HAS_SET:
            const agent = getAgent();
            recoverContacts(agent, store);
            return next(action);
        case CallTypes.MAKE_CALL: {
            const { number, endpoint } = action.payload;

            const success = () => next(action);

            const failure = (error: string) => {
                console.log('Error at MAKE_CALL', error);
                store.dispatch(
                    addError(JSON.parse(error).message || 'Oops, there was an error while trying to connect the call')
                );
            };

            const getEndpoint = (number: string) => {
                const countryAbbreviation = store.getState().app.appConfig?.defaultCountryCode;

                const { formattedNumber, countryCode } = getNumberToDialThroughConnect(number, countryAbbreviation);

                console.log(`Connecting ${formattedNumber} in ${countryCode}`);

                return connect.Endpoint.byPhoneNumber(
                    formatPhoneNumber(formattedNumber, PhoneLib.PhoneNumberFormat.E164, countryCode)
                );
            };

            // Check if reallocation of outbound number is needed
            const {
                app: { ID, instance, appConfig },
                auth: { token },
            } = store.getState();

            // Check if Number hits numberMapping condition
            const numberMapping = appConfig!.outboundNumberMappings?.find(mapping => {
                return mapping.number === number;
            });

            // Check if number should hit SIP gateway condition
            const gatewayConfig = appConfig?.SIPgatewayConfig;
            const isGatewayNumber = gatewayConfig?.conditions.some(v => {
                return matchPattern(v.regexPattern, number);
            });
            const countryCodeExclusion = gatewayConfig?.exclusions?.countryCodes?.some(countryCode => {
                return isValidNumberForRegion(number!, countryCode);
            });
            const regexExclusion = gatewayConfig?.exclusions?.regexPatterns?.some(regexPattern => {
                return matchPattern(regexPattern, number);
            });
            console.log('CountryCodeExclusion', countryCodeExclusion);
            console.log('regexExclusion', regexExclusion);

            if (numberMapping) {
                store.dispatch(updateContactAttributes(ID, { 'sa-dialled-number': number }));
                getAgent()?.connect(getEndpoint(numberMapping.mapping), { success, failure });
            } else if (
                gatewayConfig &&
                isGatewayNumber &&
                (!countryCodeExclusion || number?.length === 7) &&
                !regexExclusion
            ) {
                console.log(`number ${number} hits sip gateway`);
                const data = await sipGateway(gatewayConfig, number!, token!);
                store.dispatch(updateContactAttributes(ID, { 'sa-dialled-number': number }));
                getAgent()?.connect(getEndpoint(data.gatewayE164), { success, failure });
            } else if (instance!.allocateOutbound && number) {
                allocateNumber(ID, instance!.ID, number, token!)
                    .then(response => {
                        getAgent()?.connect(getEndpoint(response.numberE164), { success, failure });
                    })
                    .catch(failure);
            } else {
                //Default make call logic
                console.log(`number ${number} hits default call logic`);
                const newEndpoint = number ? getEndpoint(number) : (endpoint as any);
                getAgent()?.connect(newEndpoint, { success, failure });
            }
            return;
        }

        case UserTypes.USER_AVAILABLE: {
            if (!getAgent()) return;
            const routableState: connect.AgentStateDefinition | undefined = getAgent()
                .getAgentStates()
                .find(state => state.type === connect.AgentStateType.ROUTABLE);

            if (!routableState || routableState.name === getAgent().getState().name) return;

            getAgent().setState(routableState, {
                success: () => next(action),
                failure: (error: string) => {
                    console.error(new Error('Error at USER_AVAILABLE'));
                    store.dispatch(
                        addError(
                            JSON.parse(error).message || 'Oops, there was an error while trying to set you to available'
                        )
                    );
                    console.log(error);
                },
            });
            return;
        }

        case UserTypes.USER_NOT_AVAILABLE: {
            if (!getAgent()) return;
            const notRoutableState: connect.AgentStateDefinition | undefined = getAgent()
                .getAgentStates()
                .find(state => state.type === connect.AgentStateType.OFFLINE);
            if (!notRoutableState) return;
            getAgent().setState(notRoutableState, {
                success: () => next(action),
                failure: (error: string) => {
                    console.error(new Error('Error at USER_NOT_AVAILABLE'));
                    store.dispatch(
                        addError(
                            JSON.parse(error).message ||
                                'Oops, there was an error while trying to set you to not available'
                        )
                    );
                    console.log(error);
                },
            });
            return;
        }

        case CallTypes.MUTE:
            getAgent()?.mute();

            websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_MUTE_SUCCESS);

            return next(action);
        case CallTypes.UNMUTE:
            getAgent()?.unmute();

            websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_UNMUTE_SUCCESS);

            return next(action);

        case UserTypes.USER_SET_STATE: {
            if (store.getState().global.changingState) {
                return;
            }
            store.dispatch(setChangingState(action.payload.state.name));

            return getAgent()?.setState(
                action.payload.state,
                {
                    success: () => {
                        store.dispatch(setChangingState(''));
                        next(action);
                    },
                    failure: (error: string) => {
                        const errorMap: { [key: string]: string } = {
                            'Network Failure':
                                'Unable to update status. There is an issue with your internet connection, refreshing the tab may fix this issue',
                        };

                        Logger.error('SET AGENT STATE ERROR', new Error(error));

                        const errorMsg = errorMap[JSON.parse(error).message];
                        if (errorMsg) {
                            store.dispatch(addError(errorMsg));
                        } else {
                            store.dispatch(addError('Unable to update status'));
                        }

                        store.dispatch(setChangingState(''));
                    },
                },
                { enqueueNextState: true }
            );
        }

        case UserTypes.GET_QUICK_CONNECTS: {
            getAgent()?.getEndpoints(getAgent().getAllQueueARNs(), {
                success: (response: any) => {
                    action.payload = response.addresses;
                    return next(action);
                },
                failure: (error: string) => {
                    console.error(new Error('Error at GET_QUICK_CONNECTS'));
                    store.dispatch(
                        addError(
                            JSON.parse(error).message ||
                                'Oops, there was an error while trying to retrieve your Quick Connects'
                        )
                    );
                },
            });
            return;
        }

        default:
            return next(action);
    }
};

export default ccpAgentMiddleware;
